import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { loginAdmin } from "@/lib/auth";
import loginHero from "@/assets/login_hero.png";

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      await loginAdmin(email, password);
      toast.success("Logged in successfully");
      navigate({ to: "/" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Login failed. Please try again.";
      console.error('Login error:', error);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Left Side - Marketing */}
      <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center bg-gradient-to-br from-background to-muted/20 px-12">
        <div className="text-center space-y-6 max-w-lg">
          <div>
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-lg bg-primary text-2xl font-black text-primary-foreground">
              Q
            </div>
            <h1 className="text-5xl font-bold text-foreground">
              Deuxda
            </h1>
            <p className="mt-4 text-2xl font-semibold text-foreground">
              Cheaper
            </p>
            <p className="text-2xl font-semibold text-primary">
              but Cash
            </p>
          </div>
          <img
            src={loginHero}
            alt="Deuxda Marketplace"
            className="w-full max-w-sm rounded-lg shadow-lg"
          />
          <p className="text-sm text-muted-foreground">
            Manage your marketplace with powerful admin tools and real-time insights
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center px-4 py-8 lg:px-12">
        <div className="w-full max-w-md space-y-6">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-xl font-black text-primary-foreground">
              Q
            </div>
            <h1 className="text-2xl font-bold text-foreground">Deuxda Admin</h1>
            <p className="mt-1 text-sm text-muted-foreground">Marketplace Administration</p>
          </div>

          {/* Form Header */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Log in/Register</h2>
            <p className="text-sm text-muted-foreground">Log in to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">
                  Password
                </Label>
                <a
                  href="#"
                  className="text-xs text-primary hover:underline font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    toast.info("Password reset not yet implemented");
                  }}
                >
                  Forgot your password?
                </a>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 pr-10 h-11"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              className="w-full h-11 text-base font-medium"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Log in"}
            </Button>
          </form>


          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border/30"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          {/* OAuth Buttons */}
          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 flex items-center justify-center gap-2"
              disabled={loading}
              onClick={() => toast.info("OAuth integration coming soon")}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 flex items-center justify-center gap-2"
              disabled={loading}
              onClick={() => toast.info("OAuth integration coming soon")}
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.05 13.5c-.91 0-1.75.46-2.25 1.13.97 1.5 1.54 3.29 1.54 5.21 0 .5-.04.99-.11 1.47h4.76c.84 0 1.52-.68 1.52-1.52V14c0-.83-.67-1.5-1.5-1.5h-.96zm-11 0H4c-.84 0-1.52.68-1.52 1.52V19c0 .84.68 1.52 1.52 1.52h2.05c-.07-.48-.11-.97-.11-1.47 0-1.92.57-3.71 1.54-5.21-.5-.67-1.34-1.13-2.25-1.13z"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 1.5c2.49 0 4.5 2.01 4.5 4.5S14.49 15 12 15s-4.5-2.01-4.5-4.5 2.01-4.5 4.5-4.5z"/>
              </svg>
              Continue with Apple
            </Button>
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              Support:{" "}
              <a href="mailto:admin@deuxda.co" className="text-primary hover:underline font-medium">
                admin@deuxda.co
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Login — Deuxda Admin" },
      { name: "description", content: "Sign in to Deuxda Admin Dashboard" },
      { property: "og:title", content: "Login — Deuxda Admin" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: LoginPage,
});
